// src/pages/verificaciones/VerificacionesPage.tsx
import React, { useCallback, useEffect, useState } from "react";
import {
  verificationAdminApi,
  VerificationDocumentAdminItem,
  VerificationDocumentStatus,
  VerificationDocumentType,
} from "../../api/verificationAdminApi";

const STATUS_LABEL: Record<VerificationDocumentStatus, string> = {
  PENDING_UPLOAD: "Sin subir",
  PENDING_REVIEW: "Pendiente",
  APPROVED: "Aprobado",
  REJECTED: "Rechazado",
  EXPIRED: "Vencido",
};

const TYPE_LABEL: Record<VerificationDocumentType, string> = {
  IDENTITY: "Documento de identidad",
  SELFIE: "Foto / selfie",
  RUT: "RUT",
  CHAMBER_OF_COMMERCE: "Cámara de Comercio",
};

function StatusBadge({ status }: { status: VerificationDocumentStatus }) {
  const color =
    status === "APPROVED"
      ? "#0cbb1d"
      : status === "REJECTED"
        ? "#D60D4C"
        : status === "PENDING_REVIEW"
          ? "#f98115"
          : "#9AA6AD";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "3px 10px",
        borderRadius: 20,
        fontSize: "0.72rem",
        fontWeight: 700,
        background: `${color}1F`,
        color,
      }}
    >
      <i
        className={`bi ${
          status === "APPROVED"
            ? "bi-patch-check-fill"
            : status === "REJECTED"
              ? "bi-x-circle-fill"
              : "bi-hourglass-split"
        }`}
      />
      {STATUS_LABEL[status]}
    </span>
  );
}

type ModalState = {
  item: VerificationDocumentAdminItem;
  accion: "aprobar" | "rechazar" | null;
  motivo: string;
  saving: boolean;
};

export default function VerificacionesPage() {
  const [items, setItems] = useState<VerificationDocumentAdminItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<VerificationDocumentStatus | "">(
    "PENDING_REVIEW",
  );
  const [typeFilter, setTypeFilter] = useState<VerificationDocumentType | "">("");
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState | null>(null);
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [viewerError, setViewerError] = useState(false);

  const openViewer = (url: string) => {
    setViewerError(false);
    setViewerUrl(url);
  };

  const PAGE_SIZE = 20;

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await verificationAdminApi.listar(page, PAGE_SIZE, statusFilter, typeFilter);
      setItems(res.data?.content ?? []);
      setTotal(res.data?.totalElements ?? 0);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, typeFilter]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleDecision = useCallback(async () => {
    if (!modal || !modal.accion) return;
    if (modal.accion === "rechazar" && !modal.motivo.trim()) {
      alert("Debes indicar el motivo del rechazo.");
      return;
    }

    setModal((m) => (m ? { ...m, saving: true } : m));
    try {
      if (modal.accion === "aprobar") {
        await verificationAdminApi.aprobar(modal.item.id);
        setToast(`✅ Documento de "${modal.item.userFullName}" aprobado.`);
      } else {
        await verificationAdminApi.rechazar(modal.item.id, modal.motivo.trim());
        setToast(`❌ Documento de "${modal.item.userFullName}" rechazado.`);
      }
      setTimeout(() => setToast(null), 4000);
      setModal(null);
      fetchList();
    } catch (e: any) {
      alert(e?.response?.data?.mensaje || "Error al procesar la decisión");
      setModal((m) => (m ? { ...m, saving: false } : m));
    }
  }, [modal, fetchList]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">
          <i className="bi bi-patch-check-fill me-2" style={{ color: "var(--fg-green)" }} />
          Verificación de usuarios
        </h1>
        <p className="page-subtitle">
          Identidad, selfie, RUT y Cámara de Comercio de compradores y vendedores.
        </p>
      </div>

      {toast && (
        <div
          style={{
            position: "fixed",
            top: 20,
            right: 20,
            zIndex: 9999,
            background: "#1a7a2e",
            color: "#fff",
            padding: "12px 20px",
            borderRadius: 8,
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            fontSize: "0.9rem",
            fontWeight: 600,
          }}
        >
          {toast}
        </div>
      )}

      <div className="fg-card mb-3" style={{ padding: "14px 18px" }}>
        <div className="d-flex gap-3 flex-wrap align-items-center">
          <div>
            <label style={{ fontSize: "0.75rem", fontWeight: 700, color: "var(--fg-muted)", display: "block", marginBottom: 4 }}>
              Estado
            </label>
            <select
              className="form-select form-select-sm"
              style={{ borderRadius: 8, minWidth: 160 }}
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as VerificationDocumentStatus | "");
                setPage(0);
              }}
            >
              <option value="">Todos</option>
              {(Object.keys(STATUS_LABEL) as VerificationDocumentStatus[]).map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: "0.75rem", fontWeight: 700, color: "var(--fg-muted)", display: "block", marginBottom: 4 }}>
              Tipo de documento
            </label>
            <select
              className="form-select form-select-sm"
              style={{ borderRadius: 8, minWidth: 200 }}
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value as VerificationDocumentType | "");
                setPage(0);
              }}
            >
              <option value="">Todos</option>
              {(Object.keys(TYPE_LABEL) as VerificationDocumentType[]).map((t) => (
                <option key={t} value={t}>
                  {TYPE_LABEL[t]}
                </option>
              ))}
            </select>
          </div>
          <span style={{ marginLeft: "auto", color: "var(--fg-muted)", fontSize: "0.82rem" }}>
            {total} resultado(s)
          </span>
        </div>
      </div>

      <div className="fg-card" style={{ overflow: "hidden", padding: 0 }}>
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead style={{ background: "var(--fg-bg)", fontSize: "0.78rem" }}>
              <tr>
                <th style={{ padding: "10px 16px" }}>Usuario</th>
                <th style={{ padding: "10px 16px" }}>Documento</th>
                <th style={{ padding: "10px 16px" }}>Estado</th>
                <th style={{ padding: "10px 16px" }}>Subido</th>
                <th style={{ padding: "10px 16px" }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={5} style={{ textAlign: "center", padding: 40, color: "var(--fg-muted)" }}>
                    <div className="spinner-border spinner-border-sm text-success me-2" />
                    Cargando…
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ textAlign: "center", padding: 40, color: "var(--fg-muted)" }}>
                    <i className="bi bi-inbox" style={{ fontSize: "2rem", display: "block", marginBottom: 8 }} />
                    No hay documentos que coincidan con el filtro.
                  </td>
                </tr>
              )}
              {!loading &&
                items.map((item) => (
                  <tr key={item.id} style={{ fontSize: "0.875rem" }}>
                    <td style={{ padding: "12px 16px", verticalAlign: "middle" }}>
                      <div style={{ fontWeight: 600 }}>{item.userFullName || "—"}</div>
                      <div style={{ fontSize: "0.72rem", color: "var(--fg-muted)" }}>{item.userId}</div>
                    </td>
                    <td style={{ padding: "12px 16px", verticalAlign: "middle" }}>
                      {TYPE_LABEL[item.documentTypeCode]}
                    </td>
                    <td style={{ padding: "12px 16px", verticalAlign: "middle" }}>
                      <StatusBadge status={item.status} />
                      {item.status === "REJECTED" && item.rejectionReason ? (
                        <div style={{ fontSize: "0.72rem", color: "var(--fg-red)", marginTop: 4 }}>
                          {item.rejectionReason}
                        </div>
                      ) : null}
                    </td>
                    <td style={{ padding: "12px 16px", verticalAlign: "middle", fontSize: "0.8rem", color: "var(--fg-muted)" }}>
                      {item.uploadedAt ? new Date(item.uploadedAt).toLocaleString("es-CO") : "—"}
                    </td>
                    <td style={{ padding: "12px 16px", verticalAlign: "middle" }}>
                      <div className="d-flex gap-2">
                        {item.downloadUrl && (
                          <button
                            onClick={() => openViewer(item.downloadUrl!)}
                            className="btn-fg-outline"
                            style={{ padding: "4px 10px", fontSize: "0.78rem" }}
                          >
                            <i className="bi bi-eye me-1" />
                            Ver
                          </button>
                        )}
                        {item.status === "PENDING_REVIEW" && (
                          <>
                            <button
                              onClick={() =>
                                setModal({ item, accion: "aprobar", motivo: "", saving: false })
                              }
                              className="btn-fg-success"
                              style={{ padding: "4px 10px", fontSize: "0.78rem" }}
                            >
                              <i className="bi bi-check-lg me-1" />
                              Aprobar
                            </button>
                            <button
                              onClick={() =>
                                setModal({ item, accion: "rechazar", motivo: "", saving: false })
                              }
                              className="btn-fg-danger"
                              style={{ padding: "4px 10px", fontSize: "0.78rem" }}
                            >
                              <i className="bi bi-x-lg me-1" />
                              Rechazar
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, padding: "12px 16px", borderTop: "1px solid var(--fg-bg)" }}>
            <button disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="btn-fg-outline" style={{ padding: "4px 12px" }}>
              ‹ Anterior
            </button>
            <span style={{ fontSize: "0.82rem", color: "var(--fg-muted)" }}>
              Pág. {page + 1} / {totalPages}
            </span>
            <button disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)} className="btn-fg-outline" style={{ padding: "4px 12px" }}>
              Siguiente ›
            </button>
          </div>
        )}
      </div>

      {/* Visor de documento */}
      {viewerUrl && (
        <>
          <div onClick={() => setViewerUrl(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 3000 }} />
          <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 3010, background: "#fff", borderRadius: 12, width: "90vw", maxWidth: 800, height: "85vh", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 16px 48px rgba(0,0,0,0.35)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", borderBottom: "1px solid #f0f0f0" }}>
              <strong>Documento de verificación</strong>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <a
                  href={viewerUrl}
                  download
                  title="Descargar archivo"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "6px 12px",
                    borderRadius: 6,
                    border: "1px solid #ddd",
                    color: "var(--fg-text)",
                    textDecoration: "none",
                    fontSize: "0.8rem",
                  }}
                >
                  <i className="bi bi-download" />
                  Descargar
                </a>
                <button onClick={() => setViewerUrl(null)} style={{ background: "none", border: "none", fontSize: "1.3rem", cursor: "pointer" }}>
                  <i className="bi bi-x-lg" />
                </button>
              </div>
            </div>
            <div style={{ flex: 1, background: "#1a1a1a", display: "flex", alignItems: "center", justifyContent: "center", overflow: "auto" }}>
              {viewerError ? (
                <div style={{ color: "#fff", textAlign: "center", padding: 32, maxWidth: 480 }}>
                  <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: "2.2rem", color: "var(--fg-orange)", display: "block", marginBottom: 12 }} />
                  <p style={{ fontWeight: 700, marginBottom: 6 }}>No se pudo cargar el documento</p>
                  <p style={{ fontSize: "0.85rem", color: "#ccc", marginBottom: 14 }}>
                    S3 rechazó la solicitud (403 Access Denied). Esto ocurre cuando las credenciales de AWS del backend
                    no tienen permiso vigente para leer el bucket — revisa el usuario IAM en la consola de AWS.
                  </p>
                  <a href={viewerUrl} target="_blank" rel="noreferrer" style={{ color: "#7fd0ff", fontSize: "0.82rem" }}>
                    Abrir la URL directamente para ver el error completo
                  </a>
                </div>
              ) : viewerUrl.toLowerCase().includes(".pdf") ? (
                <iframe src={viewerUrl} title="documento" style={{ width: "100%", height: "100%", border: "none" }} />
              ) : (
                <img
                  src={viewerUrl}
                  alt="documento"
                  onError={() => setViewerError(true)}
                  style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                />
              )}
            </div>
          </div>
        </>
      )}

      {/* Modal de decisión */}
      {modal && (
        <>
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 2000 }} />
          <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 2010, background: "#fff", borderRadius: 12, padding: 28, width: 440, maxWidth: "95vw", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
            <h5 style={{ margin: "0 0 16px", fontWeight: 700 }}>
              {modal.accion === "aprobar" ? "Aprobar" : "Rechazar"} documento
            </h5>
            <p style={{ color: "var(--fg-muted)", fontSize: "0.88rem", marginBottom: 16 }}>
              {TYPE_LABEL[modal.item.documentTypeCode]} de <strong>{modal.item.userFullName}</strong>
            </p>

            {modal.accion === "rechazar" && (
              <>
                <label style={{ fontWeight: 600, fontSize: "0.85rem", display: "block", marginBottom: 6 }}>
                  Motivo del rechazo *
                </label>
                <textarea
                  value={modal.motivo}
                  onChange={(e) => setModal((m) => (m ? { ...m, motivo: e.target.value } : m))}
                  placeholder="Ej: La foto del documento no es legible."
                  style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #ddd", minHeight: 90, fontSize: "0.88rem" }}
                />
              </>
            )}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 18 }}>
              <button onClick={() => setModal(null)} disabled={modal.saving} className="btn-fg-outline" style={{ padding: "8px 18px" }}>
                Cancelar
              </button>
              <button
                onClick={handleDecision}
                disabled={modal.saving || (modal.accion === "rechazar" && !modal.motivo.trim())}
                className={modal.accion === "aprobar" ? "btn-fg-success" : "btn-fg-danger"}
                style={{ padding: "8px 18px" }}
              >
                {modal.saving ? <span className="spinner-border spinner-border-sm" /> : modal.accion === "aprobar" ? "Confirmar aprobación" : "Confirmar rechazo"}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
